import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { MultiSelect, type Option } from 'react-multi-select-component';

type Props = {
  className?: string;
  name: string;
  label: string | React.ReactNode;
  placeholder?: string;
  placeholderSearch?: string | undefined;
  filterOptions?: (options: Option[], filter: string) => Promise<Option[]>;
  debounceDuration?: number;
  children?: React.ReactNode;
};

/**
 * Due to the "filterOptions" prop nature of react-multi-select-component, some trickery and hacking were needed to make this work with async fetching of options.
 */

export const Autocomplete = ({ className, label, name, placeholder, placeholderSearch = '', filterOptions, debounceDuration = 1000, children }: Props) => {
  const { control, setValue, watch } = useFormContext();
  const { t } = useTranslation();
  const [selected, setSelected] = React.useState<Option[]>([]);
  const localizedPlaceholder = React.useMemo(() => placeholder || t('actions.choose'), [t, placeholder]);
  const fieldValue = watch(name);

  React.useEffect(() => {
    if (!fieldValue) setSelected([]);
  }, [fieldValue]);

  const onChange = (selection: Option[]) => {
    const nextSelected: Option[] = selection.length ? [selection.at(-1) as Option] : [];
    setSelected(nextSelected);
    const nextValue = nextSelected.length ? nextSelected[0].value : '';
    setValue(name, nextValue, { shouldDirty: true, shouldTouch: true });
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value: _value, onChange: _onChange } }) => {
        // console.log('render', _value);
        /**
         * Unfortunately, the value expected is of type Option ({label: string, value: string}), but here we get the string value as submitted by the form to the API endpoint.
         * From it, we have no way to know the label associated, since it's kept internally in the component; so we are relying on the last selected value saved in the useState hook.
         * This will cause issues with the initial value, since it will not be set correctly, and the reset of values which will not work as expected (the internal state will not be updated on filters reset).
         */

        return (
          <div className={className} data-testid={`control-${name}`}>
            {label && (
              <label className="control-label" htmlFor={`input-${name}`}>
                {label}
              </label>
            )}
            <MultiSelect
              labelledBy={`control-${name}`}
              options={[]}
              hasSelectAll={false}
              ClearSelectedIcon={null}
              overrideStrings={{ selectSomeItems: localizedPlaceholder, search: placeholderSearch }}
              closeOnChangedValue={true}
              filterOptions={filterOptions}
              debounceDuration={debounceDuration}
              value={selected}
              onChange={onChange}
            />
            {children}
          </div>
        );
      }}
    />
  );
};
